'use client'
import { Suspense, useState } from 'react'
import { useSearchParams } from 'next/navigation'
import type { ReactNode } from 'react'
import { startWidgetFlow } from '@/features/widget/startWidgetFlow'
import type { LinkField, SbBaseBlockProps } from '@/services/storyblok/storyblok'
import { getLinkFieldURL } from '@/services/storyblok/Storyblok.helpers'
import { useRoutingLocale } from '@/utils/l10n/useRoutingLocale'
import { storyblokEditable } from '@storyblok/react'
import { Button, type ButtonProps } from 'ui/src/components/Button/Button'

type Props = SbBaseBlockProps<
  {
    text: string
    link: LinkField
  } & Pick<StartButtonProps, 'size' | 'fullWidth'>
>

export function WidgetFlowStartButtonBlock({ blok }: Props) {
  const { link, text, fullWidth = true, size = 'large' } = blok

  return (
    <Suspense>
      <StartButton {...storyblokEditable(blok)} href={getLinkFieldURL(link)} size={size} fullWidth={fullWidth}>
        {text}
      </StartButton>
    </Suspense>
  )
}

type StartButtonProps = {
  href: string
  children: ReactNode
  size: ButtonProps<'button'>['size']
  fullWidth: ButtonProps<'button'>['fullWidth']
} & ReturnType<typeof storyblokEditable>

function StartButton({ href, children, ...forwardedProps }: StartButtonProps) {
  const [loading, setLoading] = useState(false)
  const locale = useRoutingLocale()
  const searchParams = useSearchParams()
  const path = new URL(href).pathname

  async function handleClick() {
    setLoading(true)
    const slug = path.replace(`/${locale}/`, '')
    await startWidgetFlow(locale, slug, searchParams.toString())
  }

  return (
    <Button {...forwardedProps} onClick={handleClick} variant="primary" size="large" loading={loading}>
      {children}
    </Button>
  )
}
